/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import {
  CSSProperties,
  Fragment,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from "react";
import { LinearProgress, Tooltip } from "@mui/material";
import { PALETTE } from "styles/base/variables";
import { convertMobileNo } from "utils/converter";
import {
  DerivedRow,
  STATUS_META,
  StatusCode,
  statusKind
} from "models/payrollManagement.model";
import {
  CycleCode,
  GROUPS,
  GridColumn,
  GridGroup,
  groupDueText,
  isColumnInScope,
  NOTE_GROUP_COLUMN_IDS
} from "./grid.config";
import {
  extractEtcNotices,
  groupState,
  noteGroupCode,
  openMemos,
  rowHasAlert,
  rowIsDone
} from "./grid.logic";

// 매트릭스 전용 색 — PALETTE에 없는 값이라 여기서만 로컬로 둔다.
const C = {
  good: PALETTE.progressCompleted, // 완료(초록)
  warn: "#F5A623", // 조치 필요(주황)
  crit: PALETTE.error, // 확인요망(빨강)
  ink: PALETTE.text, // 진한 글자 (거래처명·컬럼명)
  ink2: "#52514E", // 그룹/컬럼 헤더 글자
  muted: PALETTE.textMid, // 보조 글자 (담당자·마감일)
  dash: PALETTE.textSub, // 해당없음 '–'
  grid: PALETTE.borderLight,
  rule: PALETTE.border,
  headBg: "#FCFCFB",
  rowHi: PALETTE.primaryHover
};

const ROW_H = 34;
// 고정 왼쪽 블록 폭. 체크 컬럼은 발송 선택 모드에서만 노출되므로 offset을 동적으로 계산한다.
const W = { flag: 5, chk: 34, name: 288, owner: 84, type: 52, basis: 72 };
type LeftOffsets = {
  flag: number;
  chk: number;
  name: number;
  owner: number;
  type: number;
  basis: number;
};
const leftOffsets = (selectable: boolean): LeftOffsets => {
  const chk = selectable ? W.chk : 0;
  return {
    flag: 0,
    chk: W.flag,
    name: W.flag + chk,
    owner: W.flag + chk + W.name,
    type: W.flag + chk + W.name + W.owner,
    basis: W.flag + chk + W.name + W.owner + W.type
  };
};

// 고정 열의 position/left/z-index/width는 인라인 style로 준다.
// 테이블 css 블록의 하위 선택자(thead th 등)가 css prop보다 우선이라 z-index 싸움에서 지기 때문.
const fixedWidth = (width: number): CSSProperties => ({
  width,
  minWidth: width,
  maxWidth: width,
  boxSizing: "border-box"
});
// z-index 계층: 본문 데이터(auto) < 고정 본문(2) < 일반 헤더(4) < 고정 헤더(6).
// 6 이하로 유지해 페이지의 드롭다운/모달(z-index 20+)에 가려지지 않게 한다.
const pinBody = (
  leftPx: number,
  width: number,
  extra?: CSSProperties
): CSSProperties => ({
  position: "sticky",
  left: leftPx,
  zIndex: 2,
  background: PALETTE.light,
  ...fixedWidth(width),
  ...extra
});
const pinHead = (
  leftPx: number,
  width: number,
  top: number,
  extra?: CSSProperties
): CSSProperties => ({
  position: "sticky",
  left: leftPx,
  top,
  zIndex: 6,
  background: C.headBg,
  ...fixedWidth(width),
  ...extra
});

export interface SortState {
  key: string; // "name" | "incharge" | "type" | "basis" | "grp:<id>"
  dir: 1 | -1;
}

interface PayrollGridProps {
  rows: DerivedRow[];
  viewYear: number;
  viewMonth: number;
  loading?: boolean;
  sort: SortState;
  onSort: (key: string) => void;
  collapsed: Set<string>;
  onToggleCollapse: (groupId: string) => void;
  // 납부서 카톡 발송 선택 모드에서만 체크 컬럼을 노출하고, 이때 셀 클릭 동작은 모두 막힌다.
  selectable?: boolean;
  selected: Set<string>;
  onToggleSelected: (businessId: string) => void;
  onToggleAll: (checked: boolean) => void;
  // 드래그로 훑은 구간을 한 번에 선택/해제 (엑셀식 드래그 선택)
  onSelectRange: (businessIds: string[], checked: boolean) => void;
  // "납부서 및 접수증" 칸 클릭 → 그 거래처의 납부서 목록 팝업
  onNoticeCellClick: (row: DerivedRow) => void;
  // 발송 그룹의 번호 칸 클릭 → 알림톡 수신 번호 변경 팝업
  onPhoneClick: (row: DerivedRow) => void;
  // 발송 칸이 초록(발송 완료)일 때 클릭 → 발송한 알림톡 내용 보기 팝업
  onDispatchClick: (row: DerivedRow) => void;
  onManualToggle: (row: DerivedRow, colId: string, nextDone: boolean) => void;
  onRetClick: (row: DerivedRow) => void;
  onMemoOpen: (row: DerivedRow, anchor: HTMLElement) => void;
  onNameClick: (row: DerivedRow, anchor: HTMLElement) => void;
}

const colMinWidth = (col: GridColumn) => col.minWidth ?? (col.wide ? 60 : 34);

// 담당자 수동 체크 컬럼 → [미완료코드, 완료코드]
const MANUAL_PAIR: Record<string, [StatusCode, StatusCode]> = {};
GROUPS.forEach((g) =>
  g.cols.forEach((c) => {
    if (!c.manualCell) return;
    MANUAL_PAIR[c.id] =
      c.id.startsWith("s_") || c.id.startsWith("q_")
        ? ["nsub", "sub"]
        : ["nfile", "filed"];
  })
);

function Dot({ code }: { code: StatusCode }) {
  const meta = STATUS_META[code] || STATUS_META.off;
  if (code === "off") {
    return (
      <span
        css={css`
          color: ${C.muted};
          opacity: 0.5;
          font-size: 12px;
        `}
      >
        –
      </span>
    );
  }
  const color =
    meta.dot === "done" ? C.good : meta.dot === "alert" ? C.crit : C.warn;
  const filled = meta.dot === "done" && code !== "refund";
  return (
    <span
      css={css`
        display: inline-block;
        width: 11px;
        height: 11px;
        border-radius: 3px;
        position: relative;
        ${filled
          ? `background:${color};`
          : `box-shadow: inset 0 0 0 2px ${color};`}
        ${meta.dot === "alert" &&
        `background:${C.crit};&::before{content:"!";position:absolute;inset:0;display:grid;place-items:center;color:#fff;font-size:8px;font-weight:900;line-height:1;}`}
      `}
    />
  );
}

const GridRow = memo(function GridRow({
  row,
  index,
  viewMonth,
  collapsed,
  selectable,
  left,
  isSelected,
  onToggleSelected,
  onDragStart,
  onDragEnter,
  onManualToggle,
  onRetClick,
  onMemoOpen,
  onNameClick,
  onNoticeCellClick,
  onPhoneClick,
  onDispatchClick,
  visibleCols
}: {
  row: DerivedRow;
  index: number;
  viewMonth: number;
  collapsed: Set<string>;
  selectable: boolean;
  left: LeftOffsets;
  isSelected: boolean;
  onToggleSelected: (id: string) => void;
  onDragStart: (index: number) => void;
  onDragEnter: (index: number) => void;
  onManualToggle: (row: DerivedRow, colId: string, nextDone: boolean) => void;
  onRetClick: (row: DerivedRow) => void;
  onMemoOpen: (row: DerivedRow, anchor: HTMLElement) => void;
  onNameClick: (row: DerivedRow, anchor: HTMLElement) => void;
  onNoticeCellClick: (row: DerivedRow) => void;
  onPhoneClick: (row: DerivedRow) => void;
  onDispatchClick: (row: DerivedRow) => void;
  visibleCols: { group: GridGroup; cols: GridColumn[] }[];
}) {
  const done = rowIsDone(row, viewMonth);
  const alert = rowHasAlert(row);
  const flagColor = alert ? C.crit : done ? "transparent" : C.warn;
  const memos = row.memos || [];
  const openCount = openMemos(row).length;
  const orgMemo = (row.orgMemo || "").trim();
  const etcNotices = extractEtcNotices(row.notices);

  const cellFor = (col: GridColumn) => {
    const code = row.cells[col.id] || "off";

    // 발송 그룹의 알림톡 수신 번호 칸.
    if (col.id === "phone") {
      const raw = row.kakaoMobileNo || row.userMobileNo || "";
      const display = raw ? convertMobileNo(raw) : "번호 없음";
      if (selectable)
        return (
          <span
            css={css`
              font-size: 11px;
              color: ${raw ? C.ink : C.muted};
            `}
          >
            {display}
          </span>
        );
      return (
        <span
          role="button"
          onClick={() => onPhoneClick(row)}
          title="클릭하여 번호 변경"
          css={css`
            font-size: 11px;
            color: ${raw ? C.ink : C.muted};
            cursor: pointer;
            border-bottom: 1px dashed transparent;
            &:hover {
              border-bottom-color: ${C.muted};
            }
          `}
        >
          {display}
        </span>
      );
    }

    // "납부서 및 접수증" 칸은 클릭하면 그 거래처의 납부서 목록 팝업을 연다 (발송 선택 모드 제외).
    const isNoticeCol = NOTE_GROUP_COLUMN_IDS.has(col.id);
    const withNoticeClick = (node: ReactNode) => {
      if (!isNoticeCol || selectable) return node;
      return (
        <span
          role="button"
          onClick={() => onNoticeCellClick(row)}
          title="납부서 목록 보기"
          css={css`
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 3px 5px;
            &:hover {
              outline: 2px solid ${PALETTE.primary}55;
              border-radius: 4px;
            }
          `}
        >
          {node}
        </span>
      );
    };

    if (col.id === "etc_notice") {
      if (!etcNotices.length)
        return withNoticeClick(
          <span
            css={css`
              color: ${C.muted};
              opacity: 0.5;
            `}
          >
            –
          </span>
        );
      return withNoticeClick(
        <Tooltip
          arrow
          placement="top"
          title={etcNotices
            .map(
              (n) =>
                `${n.taxItem || n.incomeType} ${n.amount.toLocaleString()}원`
            )
            .join(" / ")}
        >
          <span
            css={css`
              display: inline-block;
              width: 11px;
              height: 11px;
              border-radius: 3px;
              background: ${C.muted};
            `}
          />
        </Tooltip>
      );
    }

    if (col.id === "ret") {
      if (code === "off")
        return (
          <span
            css={css`
              color: ${C.muted};
              opacity: 0.5;
            `}
          >
            –
          </span>
        );
      return (
        <span
          role="button"
          onClick={selectable ? undefined : () => onRetClick(row)}
          title={`${STATUS_META[code].text} — 클릭해 세무사랑 처리 완료/해제`}
          css={css`
            ${!selectable &&
            `cursor: pointer;
             position: relative;
             &:hover {
               outline: 2px solid ${PALETTE.primary}55;
               border-radius: 4px;
             }
             &::after {
               content: "";
               position: absolute;
               right: -3px;
               bottom: -2px;
               width: 4px;
               height: 4px;
               border-radius: 50%;
               background: ${PALETTE.primary};
               opacity: 0.55;
             }`}
          `}
        >
          <Dot code={code} />
        </span>
      );
    }

    // 발송 칸이 초록(발송 완료)이면 클릭 → 발송한 알림톡 내용 팝업
    if (col.id === "dispatch" && code === "done" && !selectable) {
      return (
        <span
          role="button"
          onClick={() => onDispatchClick(row)}
          title="발송한 알림톡 내용 보기"
          css={css`
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 3px 5px;
            &:hover {
              outline: 2px solid ${PALETTE.primary}55;
              border-radius: 4px;
            }
          `}
        >
          <Dot code={code} />
        </span>
      );
    }

    const pair = MANUAL_PAIR[col.id];
    const isManual = pair && (code === pair[0] || code === pair[1]);
    if (isManual) {
      const isDoneState = code === pair[1];
      if (selectable)
        return (
          <span title={STATUS_META[code]?.text}>
            <Dot code={code} />
          </span>
        );
      return (
        <span
          role="button"
          onClick={() => onManualToggle(row, col.id, !isDoneState)}
          title={`클릭하면 ${isDoneState ? "미완료" : "완료"}로 바꿉니다`}
          css={css`
            cursor: pointer;
            position: relative;
            &:hover {
              outline: 2px solid ${PALETTE.primary}66;
              border-radius: 4px;
            }
            &::after {
              content: "";
              position: absolute;
              right: -3px;
              bottom: -2px;
              width: 4px;
              height: 4px;
              border-radius: 50%;
              background: ${PALETTE.primary};
              opacity: 0.55;
            }
          `}
        >
          <Dot code={code} />
        </span>
      );
    }

    return withNoticeClick(
      <span title={STATUS_META[code]?.text}>
        <Dot code={code} />
      </span>
    );
  };

  return (
    <tr
      onMouseDown={
        selectable
          ? (e) => {
              // 체크박스 셀에서 시작한 클릭은 checkbox onChange가 처리한다.
              if ((e.target as HTMLElement).closest("[data-checkbox-cell]"))
                return;
              e.preventDefault();
              onDragStart(index);
            }
          : undefined
      }
      onMouseEnter={selectable ? () => onDragEnter(index) : undefined}
      css={css`
        height: ${ROW_H}px;
        ${done && "opacity:.55;"}
        ${selectable &&
        `cursor: pointer; user-select: none;
         ${
           isSelected
             ? `td { background: ${PALETTE.primary}14 !important; }`
             : ""
         }`}
        &:hover {
          opacity: 1;
        }
        &:hover td {
          background: ${C.rowHi};
        }
        td {
          border-bottom: 1px solid ${C.grid};
          text-align: center;
          font-size: 12px;
          color: ${C.ink};
          background: ${PALETTE.light};
        }
      `}
    >
      <td style={pinBody(left.flag, W.flag, { padding: 0 })}>
        <span
          css={css`
            display: block;
            width: ${W.flag}px;
            height: ${ROW_H}px;
            background: ${flagColor};
          `}
        />
      </td>
      {selectable && (
        <td data-checkbox-cell style={pinBody(left.chk, W.chk)}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelected(row.businessId)}
          />
        </td>
      )}
      <td
        style={pinBody(left.name, W.name, {
          textAlign: "left",
          padding: "0 8px"
        })}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 6px;
          `}
        >
          <span
            onClick={
              selectable ? undefined : (e) => onNameClick(row, e.currentTarget)
            }
            title={row.regNumber}
            css={css`
              font-weight: 600;
              font-size: 12.5px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              border-bottom: 1px dashed transparent;
              ${!selectable &&
              `cursor: pointer;
               &:hover { border-bottom-color: ${C.muted}; }`}
            `}
          >
            {row.name}
          </span>
          {(orgMemo || memos.length > 0) && (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
                flex: none;
              `}
            >
              {orgMemo && (
                <button
                  type="button"
                  disabled={selectable}
                  onClick={
                    selectable
                      ? undefined
                      : (e) => onMemoOpen(row, e.currentTarget)
                  }
                  title={orgMemo}
                  css={css`
                    flex: none;
                    border: 1px solid ${C.good};
                    background: ${C.good}1a;
                    color: ${PALETTE.text};
                    font-size: 10px;
                    font-weight: 700;
                    border-radius: 5px;
                    padding: 1px 5px;
                    max-width: 120px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    cursor: ${selectable ? "inherit" : "pointer"};
                  `}
                >
                  {orgMemo}
                </button>
              )}
              {memos.length > 0 && (
                <button
                  type="button"
                  disabled={selectable}
                  onClick={
                    selectable
                      ? undefined
                      : (e) => onMemoOpen(row, e.currentTarget)
                  }
                  title="거래처 할 일"
                  css={css`
                    flex: none;
                    border: 1px solid ${openCount ? C.warn : C.grid};
                    background: ${openCount ? `${C.warn}22` : "transparent"};
                    color: ${PALETTE.text};
                    font-size: 10px;
                    font-weight: 700;
                    border-radius: 5px;
                    padding: 1px 5px;
                    max-width: 120px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    cursor: ${selectable ? "inherit" : "pointer"};
                  `}
                >
                  {openCount
                    ? memos.find((m) => !m.done)?.text
                    : `✓ ${memos.length}`}
                  {openCount > 1 ? ` +${openCount - 1}` : ""}
                </button>
              )}
            </div>
          )}
        </div>
      </td>
      <td style={pinBody(left.owner, W.owner)}>
        <span
          css={css`
            font-size: 11px;
            color: ${PALETTE.textMid};
          `}
        >
          {row.incharge || "-"}
        </span>
      </td>
      <td style={pinBody(left.type, W.type)}>
        <span
          title={
            row.typeLabel === "반기"
              ? "반기납부 특례 — 원천세 신고·납부서·발송만 6·12월 지급분 화면에서 진행. 지급명세서·공단업무는 매월 정상 진행"
              : ""
          }
          css={css`
            display: inline-block;
            font-size: 10px;
            padding: 1px 5px;
            border-radius: 4px;
            background: ${row.typeLabel === "반기"
              ? `${PALETTE.primary}22`
              : C.grid};
            color: ${row.typeLabel === "반기"
              ? PALETTE.primary
              : PALETTE.textMid};
          `}
        >
          {row.typeLabel}
        </span>
      </td>
      <td
        style={pinBody(left.basis, W.basis, {
          borderRight: `1px solid ${C.rule}`
        })}
      >
        <span
          title={
            row.basisLabel === "전월"
              ? "이 화면에 전월 귀속분이 담깁니다 (급여지급월 익월 + 월말일 신고 미체크)"
              : "이 화면에 당월 귀속분이 담깁니다"
          }
          css={css`
            display: inline-block;
            font-size: 10px;
            font-weight: 700;
            padding: 1px 5px;
            border-radius: 4px;
            ${row.basisLabel === "전월"
              ? `background:${PALETTE.primary}22;color:${PALETTE.primary};`
              : `border:1px solid ${C.rule};color:${PALETTE.textMid};`}
          `}
        >
          {row.basisLabel}
        </span>
      </td>

      {visibleCols.map(({ group, cols }) => {
        if (collapsed.has(group.id)) {
          // 접힌 발송 그룹은 요약 점 대신 발송 여부 셀을 그대로 보여준다 (번호 칸은 숨김).
          if (group.id === "disp") {
            const dispatchCol = group.cols.find((c) => c.id === "dispatch");
            return (
              <td
                key={group.id}
                css={css`
                  border-left: 1px solid ${C.grid};
                  min-width: 60px;
                `}
              >
                {dispatchCol && cellFor(dispatchCol)}
              </td>
            );
          }
          const gs = groupState(row, group, viewMonth);
          const gcolor =
            gs === 0
              ? C.good
              : cols.some((c) => statusKind(row.cells[c.id]) === "alert")
              ? C.crit
              : C.warn;
          // 접힌 "납부서 및 접수증" 그룹도 클릭하면 납부서 목록 팝업이 열린다.
          const noteClickable = group.id === "note" && !selectable;
          // 납부서 그룹은 전부 환급이면 초록 빈네모(refund)로 구분해 그린다.
          const noteCode = group.id === "note" ? noteGroupCode(row) : undefined;
          return (
            <td
              key={group.id}
              onClick={noteClickable ? () => onNoticeCellClick(row) : undefined}
              title={noteClickable ? "납부서 목록 보기" : undefined}
              css={css`
                border-left: 1px solid ${C.grid};
                min-width: 92px;
                ${noteClickable &&
                `cursor: pointer;
                 &:hover { background: ${PALETTE.primary}14 !important; }`}
              `}
            >
              {noteCode !== undefined ? (
                <span
                  title={noteClickable ? undefined : STATUS_META[noteCode].text}
                >
                  <Dot code={noteCode} />
                </span>
              ) : gs === 2 ? (
                <span
                  css={css`
                    color: ${C.muted};
                    opacity: 0.5;
                  `}
                >
                  –
                </span>
              ) : (
                <span
                  title={`${group.name} — ${gs === 0 ? "완료" : "미완료"}`}
                  css={css`
                    display: inline-block;
                    width: 11px;
                    height: 11px;
                    border-radius: 3px;
                    background: ${gcolor};
                  `}
                />
              )}
            </td>
          );
        }
        return cols.map((col, i) => (
          <td
            key={col.id}
            css={css`
              ${i === 0 && `border-left:1px solid ${C.grid};`}
              min-width: ${colMinWidth(col)}px;
            `}
          >
            {cellFor(col)}
          </td>
        ));
      })}
    </tr>
  );
});

function PayrollGrid({
  rows,
  viewYear,
  viewMonth,
  loading,
  sort,
  onSort,
  collapsed,
  onToggleCollapse,
  selectable = false,
  selected,
  onToggleSelected,
  onToggleAll,
  onSelectRange,
  onManualToggle,
  onRetClick,
  onMemoOpen,
  onNameClick,
  onNoticeCellClick,
  onPhoneClick,
  onDispatchClick
}: PayrollGridProps) {
  // GridRow가 memo이므로 참조가 모드마다 안정적이어야 한다.
  const left = useMemo(() => leftOffsets(selectable), [selectable]);
  const allChecked =
    rows.length > 0 && rows.every((r) => selected.has(r.businessId));

  // ── 드래그 선택 (엑셀식) ────────────────────────────────────
  // 콜백이 selected 변화마다 새 참조가 되면 GridRow memo가 매번 깨지므로 ref로 최신값을 읽는다.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const dragRef = useRef<{ start: number; checked: boolean } | null>(null);

  const applyDrag = useCallback(
    (toIndex: number) => {
      const d = dragRef.current;
      if (!d) return;
      const lo = Math.min(d.start, toIndex);
      const hi = Math.max(d.start, toIndex);
      onSelectRange(
        rowsRef.current.slice(lo, hi + 1).map((r) => r.businessId),
        d.checked
      );
    },
    [onSelectRange]
  );

  const onDragStart = useCallback(
    (index: number) => {
      const id = rowsRef.current[index]?.businessId;
      if (id === undefined) return;
      dragRef.current = { start: index, checked: !selectedRef.current.has(id) };
      applyDrag(index);
    },
    [applyDrag]
  );

  const onDragEnter = useCallback(
    (index: number) => {
      if (dragRef.current) applyDrag(index);
    },
    [applyDrag]
  );

  useEffect(() => {
    const end = () => (dragRef.current = null);
    window.addEventListener("mouseup", end);
    return () => window.removeEventListener("mouseup", end);
  }, []);

  const visibleCols = GROUPS.map((group) => ({
    group,
    cols: group.cols.filter((c) =>
      isColumnInScope(c.cycle as CycleCode | undefined, viewMonth)
    )
  })).filter((g) => g.cols.length > 0);

  const mark = (key: string) =>
    sort.key === key ? (sort.dir > 0 ? " ▲" : " ▼") : "";

  return (
    <div
      css={css`
        border: 1px solid ${C.rule};
        border-radius: 8px;
        overflow: auto;
        max-height: calc(100vh - 300px);
        position: relative;
        background: ${PALETTE.light};
        width: fit-content;
        max-width: 100%;
      `}
    >
      {loading && (
        <div
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            z-index: 8;
          `}
        >
          <LinearProgress
            sx={{
              backgroundColor: PALETTE.primaryBackground,
              "& .MuiLinearProgress-bar": { backgroundColor: PALETTE.primary }
            }}
          />
        </div>
      )}
      <table
        css={css`
          border-collapse: separate;
          border-spacing: 0;
          width: max-content;
          th,
          td {
            padding: 0;
            margin: 0;
          }
          thead th {
            position: sticky;
            background: ${C.headBg};
            font-weight: 700;
            color: ${C.ink2};
            font-size: 11.5px;
            white-space: nowrap;
            border-bottom: 1px solid ${C.rule};
            z-index: 4;
          }
        `}
      >
        <thead>
          <tr
            css={css`
              th {
                top: 0;
                height: 40px;
              }
            `}
          >
            <th style={pinHead(left.flag, W.flag, 0, { padding: 0 })} />
            {selectable && (
              <th style={pinHead(left.chk, W.chk, 0)}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => onToggleAll(e.target.checked)}
                />
              </th>
            )}
            <th
              style={pinHead(left.name, W.name, 0, {
                textAlign: "left",
                padding: "0 8px",
                cursor: "pointer"
              })}
              onClick={() => onSort("name")}
            >
              거래처{mark("name")}
            </th>
            <th
              style={pinHead(left.owner, W.owner, 0, { cursor: "pointer" })}
              onClick={() => onSort("incharge")}
            >
              담당자{mark("incharge")}
            </th>
            <th
              style={pinHead(left.type, W.type, 0, { cursor: "pointer" })}
              onClick={() => onSort("type")}
            >
              유형{mark("type")}
            </th>
            <th
              style={pinHead(left.basis, W.basis, 0, {
                borderRight: `1px solid ${C.rule}`,
                cursor: "pointer"
              })}
              onClick={() => onSort("basis")}
            >
              신고기준{mark("basis")}
            </th>
            {visibleCols.map(({ group, cols }) => {
              const isCollapsed = collapsed.has(group.id);
              const single = group.cols.length === 1;
              const due = groupDueText(group, viewYear, viewMonth);
              return (
                <th
                  key={group.id}
                  colSpan={isCollapsed ? 1 : cols.length}
                  css={css`
                    border-left: 1px solid ${C.grid};
                  `}
                >
                  <span
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      gap: 2px;
                    `}
                  >
                    <button
                      type="button"
                      onClick={() => onSort(`grp:${group.id}`)}
                      title={group.htip || group.name}
                      css={css`
                        border: 0;
                        background: transparent;
                        font: inherit;
                        font-weight: 700;
                        color: ${C.ink2};
                        cursor: pointer;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        line-height: 1.2;
                        white-space: pre-line;
                      `}
                    >
                      <span>
                        {group.headLabel || group.name}
                        {mark(`grp:${group.id}`)}
                      </span>
                      {due && (
                        <span
                          css={css`
                            font-size: 10px;
                            font-weight: 600;
                            color: ${C.muted};
                            margin-top: 1px;
                          `}
                        >
                          {due} 마감
                        </span>
                      )}
                    </button>
                    {!single && (
                      <button
                        type="button"
                        onClick={() => onToggleCollapse(group.id)}
                        title={isCollapsed ? "펼치기" : "접기"}
                        css={css`
                          border: 0;
                          background: transparent;
                          color: ${C.muted};
                          font-size: 9px;
                          cursor: pointer;
                          padding: 2px;
                        `}
                      >
                        {isCollapsed ? "▶" : "▼"}
                      </button>
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
          <tr
            css={css`
              th {
                top: 40px;
                height: 30px;
                font-weight: 500;
              }
            `}
          >
            <th style={pinHead(left.flag, W.flag, 40, { padding: 0 })} />
            {selectable && <th style={pinHead(left.chk, W.chk, 40)} />}
            <th style={pinHead(left.name, W.name, 40)} />
            <th style={pinHead(left.owner, W.owner, 40)} />
            <th style={pinHead(left.type, W.type, 40)} />
            <th
              style={pinHead(left.basis, W.basis, 40, {
                borderRight: `1px solid ${C.rule}`
              })}
            />
            {visibleCols.map(({ group, cols }) => {
              if (collapsed.has(group.id))
                return (
                  <th
                    key={group.id}
                    css={css`
                      border-left: 1px solid ${C.grid};
                    `}
                  />
                );
              return (
                <Fragment key={group.id}>
                  {cols.map((col, i) => (
                    <th
                      key={col.id}
                      css={css`
                        ${i === 0 && `border-left:1px solid ${C.grid};`}
                        min-width: ${colMinWidth(col)}px;
                        ${col.wide &&
                        "white-space:normal;word-break:keep-all;line-height:1.2;"}
                      `}
                    >
                      {col.label}
                      {col.formTag && (
                        <span
                          css={css`
                            display: block;
                            font-size: 9px;
                            color: ${C.muted};
                          `}
                        >
                          {col.formTag}
                        </span>
                      )}
                    </th>
                  ))}
                </Fragment>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row, index) => (
              <GridRow
                key={row.businessId}
                row={row}
                index={index}
                viewMonth={viewMonth}
                collapsed={collapsed}
                selectable={selectable}
                left={left}
                isSelected={selected.has(row.businessId)}
                onToggleSelected={onToggleSelected}
                onDragStart={onDragStart}
                onDragEnter={onDragEnter}
                onManualToggle={onManualToggle}
                onRetClick={onRetClick}
                onMemoOpen={onMemoOpen}
                onNameClick={onNameClick}
                onNoticeCellClick={onNoticeCellClick}
                onPhoneClick={onPhoneClick}
                onDispatchClick={onDispatchClick}
                visibleCols={visibleCols}
              />
            ))
          ) : (
            <tr>
              <td
                colSpan={40}
                css={css`
                  text-align: center;
                  padding: 60px 0;
                  color: ${PALETTE.textMid};
                  font-size: 14px;
                `}
              >
                {loading ? "데이터를 확인하는 중입니다." : "데이터가 없습니다."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default PayrollGrid;
